import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { InstitutionColors } from "@/connect/types";

// Default theme (Bridge brand) - Used before bank selection or when no colors provided
const DEFAULT_THEME: BankTheme = {
  primary: "#0F172A",
  primaryForeground: "#FFFFFF",
  accent: "#B4F132",
  surface: "#F8FAFC",
};

export interface BankTheme {
  primary: string;
  primaryForeground: string;
  accent: string;
  surface: string;
}

// Convert hex to HSL for CSS variables
function hexToHSL(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0 0% 0%";

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Apply theme to CSS variables
function applyThemeToCss(theme: BankTheme): void {
  const root = document.documentElement;

  root.style.setProperty("--bank-primary", hexToHSL(theme.primary));
  root.style.setProperty("--bank-primary-hex", theme.primary);
  root.style.setProperty(
    "--bank-primary-foreground",
    hexToHSL(theme.primaryForeground),
  );
  root.style.setProperty("--bank-accent", hexToHSL(theme.accent));
  root.style.setProperty("--bank-accent-hex", theme.accent);
  root.style.setProperty("--bank-surface", hexToHSL(theme.surface));
}

// Convert institution colors from API to BankTheme
export function institutionColorsToTheme(
  colors: InstitutionColors | null | undefined,
): BankTheme {
  if (!colors?.primaryColor) {
    return DEFAULT_THEME;
  }

  return {
    primary: colors.primaryColor,
    primaryForeground:
      colors.primaryForeground || DEFAULT_THEME.primaryForeground,
    accent: colors.accentColor || colors.primaryColor,
    surface: colors.surfaceColor || DEFAULT_THEME.surface,
  };
}

// React context for bank theme
interface BankThemeContextValue {
  theme: BankTheme;
  setTheme: (colors: InstitutionColors | null) => void;
}

const BankThemeContext = createContext<BankThemeContextValue | null>(null);

interface BankThemeProviderProps {
  children: React.ReactNode;
}

export const BankThemeProvider: React.FC<BankThemeProviderProps> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<BankTheme>(DEFAULT_THEME);

  const setTheme = useCallback((colors: InstitutionColors | null) => {
    const newTheme = institutionColorsToTheme(colors);
    setThemeState(newTheme);
  }, []);

  useEffect(() => {
    applyThemeToCss(theme);
  }, [theme]);

  // Apply default theme on mount
  useEffect(() => {
    applyThemeToCss(DEFAULT_THEME);
  }, []);

  return React.createElement(
    BankThemeContext.Provider,
    { value: { theme, setTheme } },
    children,
  );
};

export const useBankTheme = (): BankThemeContextValue => {
  const context = useContext(BankThemeContext);
  if (!context) {
    throw new Error("useBankTheme must be used within a BankThemeProvider");
  }
  return context;
};

// Get a bank's theme from institution colors (for previews, without context)
export function getBankTheme(
  colors: InstitutionColors | null | undefined,
): BankTheme {
  return institutionColorsToTheme(colors);
}

// Export default theme for use elsewhere
export { DEFAULT_THEME };
